import { ReactElement, ReactNode } from 'react';

import { randomUUID } from '@betterment/core';
import { echoLoader } from '@betterment/js-runtime';
import { ErrorElement } from '@betterment/shared-components';
import { Navigate, type RouteObject } from 'react-router-dom';

import { NotFound } from './NotFound';

import AcceptAdvisedSolo401kInvitation from '../../AcceptAdvisedSolo401kInvitation';
import { AccountPage } from '../../account/AccountPage';
import { AccountSecurityPage } from '../../account-security/AccountSecurityPage';
import { ActivityPage } from '../../activity/ActivityPage';
import AddAccount from '../../add-account/Controller';
import { AccountOwnership } from '../../add-account/self-directed-investing/account-ownership/AccountOwnership';
import { AccountConfirmation } from '../../add-account/self-directed-investing/confirmation/AccountConfirmation';
import { AccountIntroduction } from '../../add-account/self-directed-investing/introduction/Introduction';
import { InvestingExperience } from '../../add-account/self-directed-investing/investing-experience/InvestingExperience';
import { JointAccountFlow } from '../../add-account/self-directed-investing/joint-account/JointAccountFlow';
import { UserDetails } from '../../add-account/self-directed-investing/joint-account/UserDetails';
import { UserSearch } from '../../add-account/self-directed-investing/joint-account/UserSearch';
import { SdiAccountFlow } from '../../add-account/self-directed-investing/SdiAccountFlow';
import { AccountTermsAndAgreements } from '../../add-account/self-directed-investing/terms/AccountTermsAndAgreements';
import { ExistingTrustSelection } from '../../add-account/self-directed-investing/trust/ExistingTrustSelection';
import { TrustAccountFlow } from '../../add-account/self-directed-investing/trust/TrustAccountFlow';
import { TrustDetails } from '../../add-account/self-directed-investing/trust/TrustDetails';
import { ProductSelectionStep } from '../../add-account/steps/product-selection/ProductSelectionStep';
import { Instructions as AdhocDocumentVerificationInstructions } from '../../AdhocDocumentVerification/components/Instructions';
import { Success as AdhocDocumentVerificationSuccess } from '../../AdhocDocumentVerification/components/Success';
import { Upload as AdhocDocumentVerificationUpload } from '../../AdhocDocumentVerification/components/Upload';
import { AdhocDocumentVerificationFlow } from '../../AdhocDocumentVerification/FlowConfig';
import { Controller as AdvisorNetworkApp } from '../../advisor-network/Controller';
import CheckingApp from '../../apps/CheckingApp';
import GenericErrorApp from '../../apps/GenericErrorApp';
import BuyOrderFlow from '../../buy-order-flow/Controller';
import { CustomPortfolioBuilderPage } from '../../custom-portfolios/CustomPortfolioBuilderPage';
import DeferralSettingsApp from '../../DeferralSettingsApp/Controller';
import EditAddressPage from '../../edit-address-page/EditAddressPage';
import EditUserLevelSettings from '../../edit-user-level-settings/Controller';
import EmailVerificationCheckpointApp from '../../EmailVerificationCheckpoint';
import ExternallyAdvisedPlanFlow from '../../ExternallyAdvisedPlanFlow/Controller';
import { EchoRoute } from '../../features/echo/EchoRoute';
import FiveTwoNineOhioCDPurchaseApp from '../../FiveTwoNineOhioCDPurchaseApp';
import { ForecasterPage } from '../../forecaster/ForecasterPage';
import FourOhOneKSelectApp from '../../FourOhOneKAccountSelectApp';
import FourOhOneKDetailsApp from '../../FourOhOneKDetailsApp';
import Confirmation from '../../FourOhOneKDistributionFlow/Confirmation';
import DistributionFlowProvider from '../../FourOhOneKDistributionFlow/Controller';
import DisbursementMethod from '../../FourOhOneKDistributionFlow/DisbursementMethod';
import DistributionAmount from '../../FourOhOneKDistributionFlow/DistributionAmount';
import EligibilityVerification from '../../FourOhOneKDistributionFlow/EligibilityVerification';
import Withholding from '../../FourOhOneKDistributionFlow/Withholding';
import Home from '../../home/home';
import IraDetailsApp from '../../IraDetailsApp';
import { AccountPerformancePage } from '../../performance/account/AccountPerformancePage';
import { UserPerformancePage } from '../../performance/user/UserPerformancePage';
import { PurposePage } from '../../purpose/PurposePage';
import { EmergencyFundFlow } from '../../purpose-flow/purpose-details/emergency-fund/FlowConfig';
import { CoverageSelectionStep } from '../../purpose-flow/purpose-details/emergency-fund/steps/CoverageSelectionStep';
import { CustomCoverageStep } from '../../purpose-flow/purpose-details/emergency-fund/steps/CustomCoverageStep';
import { EmergencyFundNameAndImageStep } from '../../purpose-flow/purpose-details/emergency-fund/steps/EmergencyFundNameAndImageStep';
import { MonthlyExpensesStep } from '../../purpose-flow/purpose-details/emergency-fund/steps/MonthlyExpensesStep';
import { MajorPurchaseFlow } from '../../purpose-flow/purpose-details/major-purchase/FlowConfig';
import { MajorPurchaseNameAndImageStep } from '../../purpose-flow/purpose-details/major-purchase/steps/MajorPurchaseNameAndImageStep';
import { TargetStep } from '../../purpose-flow/purpose-details/major-purchase/steps/TargetStep';
import { RetirementFlow } from '../../purpose-flow/purpose-details/retirement/FlowConfig';
import { DesiredSpendingStep } from '../../purpose-flow/purpose-details/retirement/steps/DesiredSpendingStep';
import { RetirementDetailStep } from '../../purpose-flow/purpose-details/retirement/steps/RetirementDetailStep';
import { RetirementNameAndImageStep } from '../../purpose-flow/purpose-details/retirement/steps/RetirementNameAndImageStep';
import { RetirementSelectionStep } from '../../purpose-flow/purpose-details/retirement/steps/RetirementSelectionStep';
import { PurposeSelectionFlow } from '../../purpose-flow/purpose-selection/FlowConfig';
import { AssignmentStep } from '../../purpose-flow/purpose-selection/steps/AssignmentStep';
import { SelectionStep } from '../../purpose-flow/purpose-selection/steps/SelectionStep';
import ReferAFriendPage from '../../ReferAFriend/components/ReferAFriendPage';
import { SecLendingEnrollmentInvite } from '../../SecLendingEnrollmentInvite/SecLendingEnrollmentInvite';
import SellOrderFlow from '../../sell-order-flow/Controller';
import FullPageLoader from '../../Shared/components/FullPageLoader';
import { TestTrackFeatureGate } from '../../Shared/components-v2/test-track/TestTrackFeatureGate';
import { urlBuilder } from '../../Shared/urlBuilder';
import { TaxCoordinatedPortfolioFlow } from '../../tax-coordinated-portoflio-flow/FlowConfig';
import { AccountsSelection } from '../../tax-coordinated-portoflio-flow/steps/AccountsSelection';
import { EnablementIntent } from '../../tax-coordinated-portoflio-flow/steps/EnablementIntent';
import { PortfolioSelection } from '../../tax-coordinated-portoflio-flow/steps/PortfolioSelection';
import echoRoutes from '../echo-routes.json';
import { loadInitialProps } from '../loadInitialProps';

type PangeaRouteObject = RouteObject & {
  feature: string; // this attribute is necessary for statsd reporting: https://github.com/Betterment/gems/commit/d344c4b0ef5c7f1396a15509eac5bd46de5bf8e7
};

const SELF_DIRECTED_INVESTING_ENABLED = 'retail.self_directed_investing_enabled';
const BONUS_DEFERRALS_ENABLED = 'retail.px_bonus_deferrals_q3_2025_enabled';
const ADVISOR_NETWORK_ENABLED = 'retail.advisor_network_q4_2025_enabled';
const REACT_PRODUCT_SELECTION_PAGE_ENABLED = 'retail.react_product_selection_page_q1_2026_enabled';
const SDI_JOINT_AND_TRUSTS_ENABLED = 'retail.sdi_joint_and_trusts_flow_q1_2026_enabled';
const CUSTOM_PORTFOLIO_BUILDER_ENABLED = 'retail.custom_portfolio_builder_q1_2026_enabled';
const SEC_LENDING_ENROLLMENT_INVITE_ENABLED = 'retail.b4a_ax_advised_client_sec_lending_enabled';
const ADHOC_DOCUMENT_VERIFICATION_ENABLED = 'retail.adhoc_docv_q1_2026_enabled';
const PAPERLESS_DISTRIBUTION_FLOW_ENABLED = 'retail.px_paperless_distribution_withdrawal_flow_q1_2026_enabled';
const ACCOUNT_GROUPING_ENABLED = 'retail.account_grouping_q1_2026_enabled';

const withFeatureGate = (
  element: ReactElement,
  featureGate: string,
  fallback: ReactNode = <NotFound />
): ReactElement => (
  <TestTrackFeatureGate name={featureGate} context="Routes">
    <TestTrackFeatureGate.True>{element}</TestTrackFeatureGate.True>
    <TestTrackFeatureGate.False>{fallback}</TestTrackFeatureGate.False>
  </TestTrackFeatureGate>
);

export const pangeaRoutes: PangeaRouteObject[] = [
  {
    path: '/',
    element: <Home />,
    feature: 'app_home',
  },
  {
    path: `${urlBuilder.getAppChecking.path}/*`,
    element: <CheckingApp />,
    feature: 'checking_home',
  },
  {
    path: urlBuilder.appPersonalReferralInvites.path,
    element: <ReferAFriendPage />,
    feature: 'content_referrals',
    loader: loadInitialProps,
    hydrateFallbackElement: <FullPageLoader />,
  },
  {
    path: `${urlBuilder.appFiveTwoNineOhioCDPurchase.path}/*`,
    element: <FiveTwoNineOhioCDPurchaseApp />,
    feature: 'investing_five_two_nine',
  },
  {
    path: `${urlBuilder.appExternallyAdvisedPlan.path}/*`,
    element: <ExternallyAdvisedPlanFlow />,
    feature: 'investing_edit_goal',
  },
  {
    path: urlBuilder.appIraDetails.path,
    element: <IraDetailsApp />,
    feature: 'investing_goals_retirement',
  },
  {
    path: urlBuilder.appFourOhOneKDetails.path,
    element: <FourOhOneKDetailsApp />,
    feature: 'investing_four_oh_one_k',
  },
  {
    path: urlBuilder.appFourOhOneKSelect.path,
    element: <FourOhOneKSelectApp />,
    feature: 'investing_four_oh_one_k',
  },
  {
    path: `${urlBuilder.appFourOhOneKDistributionFlow.path}/*`,
    element: withFeatureGate(<DistributionFlowProvider />, PAPERLESS_DISTRIBUTION_FLOW_ENABLED),
    feature: 'investing_four_oh_one_k',
    children: [
      { path: 'eligibility_verification', element: <EligibilityVerification /> },
      { path: 'distribution_amount', element: <DistributionAmount /> },
      { path: 'withholding', element: <Withholding /> },
      { path: 'disbursement_method', element: <DisbursementMethod /> },
      { path: 'confirmation', element: <Confirmation /> },
    ],
  },
  {
    path: urlBuilder.appPersonalInfoEdit.path,
    element: <EditAddressPage />,
    feature: 'user_settings_profile_management',
    loader: loadInitialProps,
    hydrateFallbackElement: <FullPageLoader />,
  },
  {
    path: urlBuilder.appPurpose.path,
    element: <PurposePage />,
    feature: 'envelope_purpose',
  },
  {
    path: urlBuilder.appAccount.path,
    element: <AccountPage />,
    feature: 'envelope_account',
  },
  {
    path: urlBuilder.appCustomPortfolioBuilder.path,
    element: withFeatureGate(<CustomPortfolioBuilderPage />, CUSTOM_PORTFOLIO_BUILDER_ENABLED),
    feature: 'custom_portfolio_builder',
  },
  {
    path: `${urlBuilder.appPerformance.path}`,
    element: <UserPerformancePage />,
    feature: 'performance',
  },
  {
    path: `${urlBuilder.appAccountPerformance.path}`,
    element: <AccountPerformancePage />,
    feature: 'performance',
  },
  {
    path: urlBuilder.appActivity.path,
    element: <ActivityPage />,
    feature: 'investing_activity',
  },
  {
    path: urlBuilder.appAccountForecaster.path,
    element: <ForecasterPage />,
    feature: 'investing_forecaster',
  },
  {
    path: urlBuilder.appPurposeForecaster.path,
    element: <ForecasterPage />,
    feature: 'investing_forecaster',
  },
  {
    path: urlBuilder.appAccountSecurity.path,
    element: withFeatureGate(<AccountSecurityPage />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'account_security',
  },
  {
    path: urlBuilder.emailVerificationCheckpoint.path,
    element: <EmailVerificationCheckpointApp />,
    feature: 'user_settings_profile_contact_management',
  },
  {
    path: `${urlBuilder.appAccountBuyOrder.path}/*`,
    element: withFeatureGate(<BuyOrderFlow />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'direct_trading',
  },
  {
    path: `${urlBuilder.appAccountSellOrder.path}/*`,
    element: withFeatureGate(<SellOrderFlow />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'direct_trading',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/*`,
    element: <AddAccount />,
    feature: 'add_account',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/self_directed_investing`,
    element: withFeatureGate(<SdiAccountFlow />, SDI_JOINT_AND_TRUSTS_ENABLED),
    feature: 'self_directed_investing',
    children: [
      { index: true, element: <AccountIntroduction /> },
      { path: 'introduction', element: <AccountIntroduction /> },
      { path: 'ownership', element: <AccountOwnership /> },
      { path: 'investing_experience', element: <InvestingExperience /> },
      { path: 'terms_and_agreements', element: <AccountTermsAndAgreements /> },
      { path: 'confirmation', element: <AccountConfirmation /> },
      {
        path: 'joint_taxable',
        element: (
          <Navigate
            to={`${urlBuilder.appAddAccount.path}/self_directed_investing/joint_taxable/${randomUUID()}/user_search`}
            replace={true}
          />
        ),
      },
      {
        path: 'joint_taxable/:flowId',
        element: <JointAccountFlow />,
        children: [
          { path: 'user_search', element: <UserSearch /> },
          { path: 'user_details', element: <UserDetails /> },
        ],
      },
      {
        path: 'trust',
        element: (
          <Navigate
            to={`${urlBuilder.appAddAccount.path}/self_directed_investing/trust/${randomUUID()}/trust_details`}
            replace={true}
          />
        ),
      },
      {
        path: 'trust/:flowId',
        element: <TrustAccountFlow />,
        children: [
          { path: 'trust_details', element: <TrustDetails /> },
          { path: 'existing_trust_selection', element: <ExistingTrustSelection /> },
        ],
      },
    ],
  },
  {
    path: urlBuilder.appAddAccountProductSelection.path,
    element: withFeatureGate(<ProductSelectionStep />, REACT_PRODUCT_SELECTION_PAGE_ENABLED),
    feature: 'add_account',
  },
  {
    path: `${urlBuilder.appAdvisorNetwork.path}/*`,
    element: withFeatureGate(<AdvisorNetworkApp />, ADVISOR_NETWORK_ENABLED),
    feature: 'advisor_network',
  },
  {
    path: `${urlBuilder.appDeferralSettings.path}/*`,
    element: withFeatureGate(<DeferralSettingsApp />, BONUS_DEFERRALS_ENABLED),
    feature: 'investing_four_oh_one_k',
  },
  {
    path: '/generic_error',
    element: <GenericErrorApp />,
    feature: 'app_error_pages',
  },
  {
    path: `${urlBuilder.appEditUserLevelSettings.path}/*`,
    element: withFeatureGate(<EditUserLevelSettings />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'edit_user_level_settings',
  },
  {
    path: '/accept_solo_401k/*',
    element: <AcceptAdvisedSolo401kInvitation />,
    feature: 'advised_401k',
  },
  {
    path: urlBuilder.appPurposeSelection.path,
    element: <Navigate to="/purpose_selection/selection" replace={true} />,
    feature: 'purpose_selection',
  },
  {
    path: urlBuilder.appPurposeSelection.path,
    element: <PurposeSelectionFlow />,
    children: [
      { path: 'selection', element: <SelectionStep /> },
      { path: 'assignment', element: <AssignmentStep /> },
    ],
    feature: 'purpose_selection',
  },
  {
    path: urlBuilder.appAddMajorPurchasePurpose.path,
    element: <Navigate to={`/add_purpose/major_purchase/${randomUUID()}/name_and_image`} replace={true} />,
    feature: 'add_purpose',
  },
  {
    path: `${urlBuilder.appAddMajorPurchasePurpose.path}/:flowId`,
    element: <MajorPurchaseFlow />,
    children: [
      { path: 'name_and_image', element: <MajorPurchaseNameAndImageStep /> },
      { path: 'target_details', element: <TargetStep /> },
    ],
    feature: 'add_purpose',
  },
  {
    path: urlBuilder.appAddEmergencyFundPurpose.path,
    element: <Navigate to={`/add_purpose/emergency_fund/${randomUUID()}/name_and_image`} replace={true} />,
    feature: 'add_purpose',
  },
  {
    path: `${urlBuilder.appAddEmergencyFundPurpose.path}/:flowId`,
    element: <EmergencyFundFlow />,
    children: [
      { path: 'name_and_image', element: <EmergencyFundNameAndImageStep /> },
      { path: 'monthly_expenses', element: <MonthlyExpensesStep /> },
      { path: 'coverage_selection', element: <CoverageSelectionStep /> },
      { path: 'custom_coverage', element: <CustomCoverageStep /> },
    ],
    feature: 'add_purpose',
  },
  {
    path: urlBuilder.appAddRetirementPurpose.path,
    element: <Navigate to={`/add_purpose/retirement/${randomUUID()}/name_and_image`} replace={true} />,
    feature: 'add_purpose',
  },
  {
    path: `${urlBuilder.appAddRetirementPurpose.path}/:flowId`,
    element: <RetirementFlow />,
    children: [
      { path: 'name_and_image', element: <RetirementNameAndImageStep /> },
      { path: 'retirement_selection', element: <RetirementSelectionStep /> },
      { path: 'retirement_details', element: <RetirementDetailStep /> },
      { path: 'desired_spending', element: <DesiredSpendingStep /> },
    ],
    feature: 'add_purpose',
  },
  {
    path: urlBuilder.appSecuritiesLendingEnrollmentInvitation.path,
    element: withFeatureGate(<SecLendingEnrollmentInvite />, SEC_LENDING_ENROLLMENT_INVITE_ENABLED),
    feature: 'sec_lending_enrollment_invitation',
  },
  {
    path: urlBuilder.appAdhocDocumentVerification.path,
    element: <Navigate to={`${urlBuilder.appAdhocDocumentVerification.path}/instructions`} replace={true} />,
    feature: 'adhoc_document_verification',
  },
  {
    path: `${urlBuilder.appAdhocDocumentVerification.path}/*`,
    element: withFeatureGate(<AdhocDocumentVerificationFlow />, ADHOC_DOCUMENT_VERIFICATION_ENABLED),
    feature: 'adhoc_document_verification',
    children: [
      { path: 'instructions', element: <AdhocDocumentVerificationInstructions /> },
      { path: 'upload', element: <AdhocDocumentVerificationUpload /> },
      { path: 'success', element: <AdhocDocumentVerificationSuccess /> },
    ],
  },
  {
    path: urlBuilder.appTcp.path,
    element: <Navigate to={`/tcp/${randomUUID()}/enablement_intent`} replace={true} />,
    feature: 'tcp',
  },
  {
    path: `${urlBuilder.appTcp.path}/:flowId/*`,
    element: withFeatureGate(<TaxCoordinatedPortfolioFlow />, ACCOUNT_GROUPING_ENABLED),
    children: [
      { path: 'enablement_intent', element: <EnablementIntent /> },
      { path: 'accounts_selection', element: <AccountsSelection /> },
      { path: 'portfolio_selection', element: <PortfolioSelection /> },
    ],
    feature: 'tcp',
  },
  ...echoRoutes.map(
    (route): PangeaRouteObject => ({
      path: route.path.replace('/:firm_key?/app/', '/'),
      feature: route.feature,
      loader: echoLoader,
      element: <EchoRoute key={route.path} />,
      errorElement: <ErrorElement owner="web_platform" feature={route.feature} />,
      hydrateFallbackElement: <FullPageLoader />,
    })
  ),
];
